<template>
  <div :class="$style.imagePreview">
    <ImageToolbar
      :scale="scale"
      :file-name="fileName ?? ''"
      :on-close="onClose"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      @rotate-left="rotateLeft"
      @rotate-right="rotateRight"
    />
    <div
      ref="imageContainer"
      :class="$style.imageContainer"
      @wheel="handleWheel"
      @mousedown="startDrag"
      @mousemove="drag"
      @mouseup="endDrag"
      @mouseleave="endDrag"
      @click="onContainerClick"
    >
      <img
        ref="imageElement"
        :src="fileUrl"
        :alt="fileName ?? ''"
        :class="$style.previewImage"
        :style="imageStyle"
        draggable="false"
        @dragstart.prevent
        @load="onImageLoad"
        @error="emit('error')"
      />
      <FilePreviewSkeleton v-if="!isLoaded" :toolbar="false" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";

import FilePreviewSkeleton from "./FilePreviewSkeleton.vue";
import ImageToolbar from "./ImagePreviewToolbar.vue";

withDefaults(
  defineProps<{
    fileUrl: string;
    fileName?: string;
    onClose?: () => void;
  }>(),
  { fileName: undefined, onClose: undefined },
);

const emit = defineEmits<{ "content-click": []; loaded: []; error: [] }>();

const CLICK_MOVE_TOLERANCE_PX = 4;

const scale = ref(1);
const rotation = ref(0);
const position = reactive({ x: 0, y: 0 });
const isDragging = ref(false);
const dragStart = reactive({ x: 0, y: 0 });
const pointerDown = { x: 0, y: 0 };
const imageContainer = ref<HTMLElement>();
const imageElement = ref<HTMLImageElement>();
const isLoaded = ref(false);
const isFitted = ref(false);

let resizeObserver: ResizeObserver | null = null;

const imageStyle = computed(() => ({
  transform: `translate(${position.x}px, ${position.y}px) scale(${scale.value}) rotate(${rotation.value}deg)`,
  cursor: isDragging.value ? "grabbing" : "grab",
  transition: isDragging.value || !isFitted.value ? "none" : "transform 0.1s ease-out",
}));

const zoomIn = () => {
  if (scale.value < 5) {
    scale.value = Math.min(5, scale.value + 0.15);
  }
};

const zoomOut = () => {
  if (scale.value > 0.1) {
    scale.value = Math.max(0.1, scale.value - 0.15);
  }
};

const rotateLeft = () => {
  rotation.value -= 90;
};

const rotateRight = () => {
  rotation.value += 90;
};

const resetTransform = () => {
  scale.value = 1;
  rotation.value = 0;
  position.x = 0;
  position.y = 0;
};

const handleWheel = (event: WheelEvent) => {
  event.preventDefault();
  const delta = event.deltaY > 0 ? -0.1 : 0.1;
  const newScale = Math.min(5, Math.max(0.1, scale.value + delta));

  if (newScale !== scale.value) {
    const rect = imageContainer.value?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const scaleDiff = newScale - scale.value;
      position.x -= (mouseX - centerX) * scaleDiff;
      position.y -= (mouseY - centerY) * scaleDiff;
    }
    scale.value = newScale;
  }
};

const startDrag = (event: MouseEvent) => {
  isDragging.value = true;
  dragStart.x = event.clientX - position.x;
  dragStart.y = event.clientY - position.y;
  pointerDown.x = event.clientX;
  pointerDown.y = event.clientY;
  event.preventDefault();
};

const onContainerClick = (event: MouseEvent) => {
  // The second click of a double click would close the overlay the first one opened.
  if (event.detail > 1) return;
  const moved = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
  if (moved <= CLICK_MOVE_TOLERANCE_PX) emit("content-click");
};

const drag = (event: MouseEvent) => {
  if (isDragging.value) {
    position.x = event.clientX - dragStart.x;
    position.y = event.clientY - dragStart.y;
  }
};

const endDrag = () => {
  isDragging.value = false;
};

const onImageLoad = () => {
  isLoaded.value = true;
  emit("loaded");
  resetTransform();
  setImageScale();
  requestAnimationFrame(() => requestAnimationFrame(() => (isFitted.value = true)));
};

const setImageScale = () => {
  if (imageContainer.value && imageElement.value) {
    const containerRect = imageContainer.value.getBoundingClientRect();
    const img = imageElement.value;
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    if (containerWidth > 0 && containerHeight > 0 && imgWidth > 0 && imgHeight > 0) {
      const scaleX = containerWidth / imgWidth;
      const scaleY = containerHeight / imgHeight;
      const rawScale = Math.min(scaleX, scaleY, 1);
      scale.value = Math.max(0.1, Math.floor(rawScale / 0.05) * 0.05);
      return true;
    }
  }
  return false;
};

onMounted(() => {
  if (imageContainer.value) {
    resizeObserver = new ResizeObserver(() => {
      setImageScale();
    });
    resizeObserver.observe(imageContainer.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<style lang="scss" module>
.imagePreview {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.imageContainer {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--color-background-neutral-subtle);
  position: relative;
}

.previewImage {
  max-width: none;
  max-height: none;
  width: auto;
  height: auto;
  user-select: none;
  transform-origin: center;
}
</style>
